import { useState, useEffect } from 'react'
import { get, ref, onValue, orderByKey, query } from "firebase/database";
import toast from 'react-hot-toast';

import { db } from '../../utils/firebaseApp.js';

import ModalForViewFile from '../../components/utils/ModalForViewFile.js';
import ViewFileAdmin from '../../components/ViewFileAdmin.js';
import ViewFileAdmin2 from '../../components/ViewFileAdmin2.js';

import PaginationAdmin from '../DataExtraction/ExtractionFolder/PaginationAdmin.js';

import { ChevronRightIcon } from '@heroicons/react/20/solid'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCode } from '@fortawesome/free-solid-svg-icons';
import ReactJson from 'react18-json-view';
import { DocumentTextIcon, GlobeAltIcon, CalendarIcon, CubeIcon, AdjustmentsHorizontalIcon, CodeBracketIcon, ChevronUpIcon, ChevronDownIcon as SortChevronDownIcon } from '@heroicons/react/24/outline';
import { faFileExcel, faFileCsv, faRefresh, faEdit as IconEdit } from '@fortawesome/free-solid-svg-icons'
import Skeleton from 'react-loading-skeleton';
import { FileText, LinkIcon, AlertCircle, CheckCircle } from "lucide-react";
import SelectFileStatus from '../DocumentClassification/BatchFolder/SelectFileStatus.js';
import ViewFile from '../DocumentClassification/Components/ViewFile.js';

import { formatDate } from '../../utils/formatDates.js';
import classNames from '../../utils/classNames.js';

const statuses = {
    Complete: 'text-green-700 bg-green-50 ring-green-600/20',
    complete: 'text-green-700 bg-green-50 ring-green-600/20',
    finished: 'text-green-700 bg-green-50 ring-green-600/20',
    'In progress': 'text-gray-600 bg-gray-50 ring-gray-500/10',
    'in progress': 'text-gray-600 bg-gray-50 ring-gray-500/10',
    processing: 'text-gray-600 bg-gray-50 ring-gray-500/10',
    archived: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20',
    Archived: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20',
}

export default function ShowUserDetailsClassification({ userId, closeModal }) {
    // i need to call when entering this function to get the user details
    const [userDetails, setUserDetails] = useState({});

    const [classificationsList, setClassificationsList] = useState([]);
    const [selectedClassification, setSelectedClassification] = useState({});

    const [batchesList, setBatchesList] = useState([]);
    const [selectedBatch, setSelectedBatch] = useState({});
    const [currentBatchPage, setCurrentBatchPage] = useState(1);
    const itemsPerBatchPage = 10; // Number of batches per page
    const [noOfPagesBatch, setNoOfPagesBatch] = useState(10);
    const itemsPerPage = 10;

    const [files, setFiles] = useState([]);

    const [viewType, setViewType] = useState('classifications');
    const [isLoading, setIsLoading] = useState(false);

    const fetchUserDetails = async () => {
        const userDetailsRef = ref(db, 'userDetails/' + userId);
        get(userDetailsRef).then((snapshot) => {
            if (snapshot.exists()) {
                setUserDetails(snapshot.val());
            } else {
                console.log('No data available');
            }
        }).catch((error) => {
            console.error('Error fetching data:', error);
        });
    }

    const getLinkedTemplates = (classification) => {
        return classification.documentTypes.map((documentType) => {
            if (documentType.extractionId) {
                return 1;
            }
            return 0;
        }).reduce((a, b) => a + b, 0);
    }

    const fetchClassificationsList = async () => {
        try {
            // Fetch all classifications
            const classificationsListRef = ref(db, 'document_classification/classifications/' + userId);
            const classificationsSnapshot = await get(classificationsListRef);

            // Fetch all batches at once
            const batchesListRef = ref(db, 'document_classification/batches/' + userId);
            const batchesSnapshot = await get(batchesListRef);

            if (!classificationsSnapshot.exists()) {
                console.log('No classifications available');
                setClassificationsList([]);
                return;
            }

            const classificationsData = classificationsSnapshot.val() || {};
            const batchesData = batchesSnapshot.exists() ? batchesSnapshot.val() : {};

            let tempClassifications = [];

            Object.keys(classificationsData).forEach((classificationId) => {
                let classification = classificationsData[classificationId] || {};
                classification.id = classificationId;

                // Attach batches from the fetched data
                const classificationBatches = batchesData[classificationId] || {};

                // Determine extraction status based on batches
                let classificationStatus = 'complete';
                Object.values(classificationBatches).forEach((batch) => {
                    if (batch.status === 'processing') {
                        classificationStatus = 'processing';
                    }
                });

                let newClassification = {
                    name: classification.name ?? 'undefined',
                    description: classification.description ?? '',
                    status: classificationStatus,
                    documentTypes: classification.documentTypes ?? {},
                    noOfBatches: classificationBatches ? Object.keys(classificationBatches).length : 0,
                    batches: classificationBatches,
                    id: classificationId,
                    createdAt: classification.createdAt ?? Date.now(),
                    pinned: classification.pinned ?? false,
                };

                tempClassifications.push(newClassification);
            });

            // Sort the extractions by creation date
            const sortedTempClassifications = tempClassifications.sort((a, b) => Number(b.createdAt) - Number(a.createdAt));
            setClassificationsList(sortedTempClassifications);

        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const fetchBatchesList = async (extractionId) => {
        setBatchesList([]); // Clear old data
        setIsLoading(true); // Optional loading state

        const batchesListRef = ref(db, 'document_classification/batches/' + userId + '/' + extractionId);

        get(batchesListRef).then((snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                if (data != null) {
                    let tempBatches = [];

                    Object.keys(data).forEach(function (key) {
                        let batch = data[key];
                        batch.id = key;

                        let numberOfDocs = 0;
                        try {
                            numberOfDocs = batch.files ? batch.files.length : 0;
                        } catch (error) {
                            numberOfDocs = 0;
                        }

                        let newBatch = {
                            id: batch.id,
                            name: 'Batch ' + batch.id,
                            numberOfDocuments: numberOfDocs,
                            startDateTime: batch.startTime,
                            status: batch.status,
                        }

                        tempBatches.push(newBatch);
                    });

                    // Sort the batches
                    const sortedTempBatches = tempBatches.sort((a, b) => Number(b.startDateTime) - Number(a.startDateTime));

                    // Calculate pagination details
                    let totalItems = sortedTempBatches.length;
                    let tempNoOfPages = Math.ceil(totalItems / itemsPerBatchPage);
                    setNoOfPagesBatch(tempNoOfPages);

                    // Ensure current page is valid
                    if (currentBatchPage > tempNoOfPages) {
                        setCurrentBatchPage(tempNoOfPages > 0 ? tempNoOfPages : 1);
                    }

                    // Paginate the batches for the current page
                    let start = (currentBatchPage - 1) * itemsPerBatchPage;
                    let end = start + itemsPerBatchPage;
                    let paginatedBatches = sortedTempBatches.slice(start, end);

                    setBatchesList(paginatedBatches);
                    setIsLoading(false); // Optional to hide loading state
                }
            } else {
                console.log('No data available');
                setBatchesList([]);
                setIsLoading(false);
            }
        }).catch((error) => {
            console.error('Error fetching data:', error);
            setIsLoading(false);
        });
    }

    const [unsubscribeFilesListener, setUnsubscribeFilesListener] = useState(null);

    const loadFiles = (selBatch) => {
        setFiles([]);

        let queryPath = 'document_classification/files/' + userId + '/' + selectedClassification.id + '/' + selBatch.id;
        let itemsRef = ref(db, queryPath)

        // Query all files under the specified path
        const unsubscribe = onValue(itemsRef, (snapshot) => {
            const data = snapshot.val();
            let tempFiles = [];

            let countClassified = 0;
            let countUnclassified = 0;
            let countAll = 0;
            let countError = 0;
            let documentTypes = {};

            if (data) {

                //loop over classification.documentTypes and add the documentType to the documentTypes object
                if (selectedClassification?.documentTypes) {
                    selectedClassification.documentTypes.forEach((documentType) => {
                        documentTypes[documentType.name] = 0;
                    })
                }

                //initial loop to get all document types
                // Object.keys(data).forEach((key) => {
                //   let file = data[key];
                //   //if document type is not in the array, add it
                //   //every object in documentTypes is of this
                //   if (file.result?.documentType) {
                //     if (!documentTypes[file.result.documentType]) {
                //       documentTypes[file.result.documentType] = 1;
                //     } else {
                //       documentTypes[file.result.documentType]++;
                //     }
                //   }
                // })

                //console.log("before", documentTypes);

                // Loop over each file in the data
                Object.keys(data).forEach((key) => {
                    let file = data[key];
                    file.id = key;

                    // Create a new file object
                    let newFile = {
                        id: file.id,
                        name: file.name ?? 'undefined',
                        url: file.url,
                        status: file.status,
                        errorMessage: file.errorMessage || "",
                        result: file.result ?? {},
                        coordinates: file.coordinates ?? {},
                        documentInfo: file.documentInfo ?? {},
                        extraction: file.extraction ?? {},
                    }

                    //console.log(newFile);


                    // Filter files based on the current tab and status
                    if (currentTab === 'Classified' || currentTab === 'Unclassified' || currentTab === 'All' || currentTab === 'Error') {
                        if (currentTab === 'All' && (file.status === 'processed' || file.status === 'error' || file.status === 'waiting')) {
                            tempFiles.push(newFile);
                        } else if (currentTab === 'Classified' && file.status === 'processed' && file.result?.documentType !== 'other') {
                            tempFiles.push(newFile);
                        } else if (currentTab === 'Unclassified' && file.result?.documentType === 'other' && file.status === 'processed') {
                            tempFiles.push(newFile);
                        } else if (currentTab === 'Error' && file.status === 'error') {
                            tempFiles.push(newFile);
                        }

                    } else {
                        //filter dinamically based on the document type
                        //loop over the documentTypes object and check if the file.result.documentType is equal to the key
                        for (const [key, value] of Object.entries(documentTypes)) {
                            if (currentTab === key && file.result?.documentType === key) {
                                tempFiles.push(newFile);
                            }
                        }
                    }

                    // Filter files based on document type


                    // Increment the counters based on the file status
                    if (file.status === 'processed' && file.result.documentType !== 'other') countClassified++;
                    if (file.result?.documentType === 'other') countUnclassified++;
                    //increment documentTypes[file.result.documentType]
                    if (file.result?.documentType && Object.keys(documentTypes).find(key => key === file.result.documentType)) {
                        documentTypes[file.result.documentType]++;
                    }
                    if (file.status === 'error') countError++;
                    if (file.status === 'waiting' || file.status === 'processed' || file.status === 'error') countAll++;
                });

                // Apply sorting if a sort field is selected
                tempFiles = sortFiles(tempFiles);

                setFiles(tempFiles);


                let tempTabs = [];
                tempTabs.push({ name: 'All', count: countAll });
                tempTabs.push({ name: 'Classified', count: countClassified });
                tempTabs.push({ name: 'Unclassified', count: countUnclassified });
                tempTabs.push({ name: 'Error', count: countError });
                for (const [key, value] of Object.entries(documentTypes)) {
                    tempTabs.push({ name: key, count: value });
                }

                // Update the tabs state with the new counts
                setTabs(tempTabs);

                setIsLoading(false);
            }
        });

        setUnsubscribeFilesListener(() => unsubscribe);
    }

    const nextBatchPage = () => {
        if (currentBatchPage < noOfPagesBatch) {
            setCurrentBatchPage(currentBatchPage + 1);
        }
    };

    const previousBatchPage = () => {
        if (currentBatchPage > 1) {
            setCurrentBatchPage(currentBatchPage - 1);
        }
    };

    const changeView = (type) => {
        if (type === 'classifications') {
            setSelectedClassification({});

            setBatchesList([]);
            setSelectedBatch({});

            setFiles([]);
        }

        if (type === 'batches') {
            setSelectedBatch({});
            setFiles([]);
        }

        if (type === 'batch') {
            // do nothing
        }

        setViewType(type);
    }

    const changeViewType = ({ type, classificationId, batchId }) => {
        console.log("type", type);
        console.log("classificationId", classificationId);
        console.log("batchId", batchId);

        if (type === 'batches') {
            setSelectedClassification(classificationsList.find((classification) => classification.id === classificationId));
            fetchBatchesList(classificationId);
        }

        if (type === 'batch') {
            setSelectedBatch(batchesList.find((batch) => batch.id === batchId));
            // setNoOfPages(Math.ceil(selectedClassification.batches[batchId].files.length / itemsPerPage));

            const selBatchList = batchesList.find((batch) => batch.id === batchId);
            loadFiles(selBatchList);
        }

        setViewType(type);
    }

    useEffect(() => {
        if (viewType === 'batches' && selectedClassification.id) {
            fetchBatchesList(selectedClassification.id);
        }
    }, [currentBatchPage, selectedClassification.id, viewType]);

    useEffect(() => {
        fetchUserDetails();
        fetchClassificationsList();
    }, [userId]);

    function formatExtractionsDate(dateString) {
        const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(Number(dateString)).toLocaleDateString(undefined, options);
    }

    let numberOfCredits = userDetails.credits?.credits ?? 0;
    numberOfCredits = numberOfCredits / 10;
    numberOfCredits = numberOfCredits.toFixed(0);
    let emailAddress = userDetails.account?.emailAddress ?? '-';

    const [isChangingTab, setIsChangingTab] = useState(false);
    const [currentTab, setCurrentTab] = useState('All');

    // Sorting state
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'

    const [tabs, setTabs] = useState([
        { name: 'All', count: 0 },
        { name: 'Classified', count: 0 },
        { name: 'Unclassified', count: 0 },
        { name: 'Error', count: 0 },
    ]);

    const changeTab = (tab) => {
        if (tab === currentTab) return;

        setCurrentTab(tab);

        // Reset sorting when changing tabs
        setSortField(null);
        setSortDirection('desc');

        setIsChangingTab(true);
        setTimeout(() => {
            setIsChangingTab(false);
        }, 250);
    }

    // Sorting function
    const handleSort = (field) => {
        if (sortField === field) {
            // Cycle through: desc -> asc -> no sort
            if (sortDirection === 'desc') {
                setSortDirection('asc');
            } else {
                // Reset to no sorting
                setSortField(null);
                setSortDirection('desc');
            }
        } else {
            // New field, start with descending
            setSortField(field);
            setSortDirection('desc');
        }
    };

    // Sort files function
    const sortFiles = (filesToSort) => {
        if (!sortField) return filesToSort;

        return [...filesToSort].sort((a, b) => {
            if (sortField === 'confidence') {
                const aConfidence = a.result?.confidence;
                const bConfidence = b.result?.confidence;

                // Files without confidence score come first
                if (aConfidence === undefined && bConfidence === undefined) return 0;
                if (aConfidence === undefined) return -1;
                if (bConfidence === undefined) return 1;

                // Sort by confidence value
                if (sortDirection === 'desc') {
                    return bConfidence - aConfidence;
                } else {
                    return aConfidence - bConfidence;
                }
            }
            return 0;
        });
    };

    const [showFileBool, setShowFileBool] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(null);

    const openFileResult = async (file) => {
        //find the index of the file in the files array
        if (file.status === "waiting") {
            toast('File is still being processed', {
                icon: '⚠️',
            });
            return;
        } else if (file.status === "error") {
            toast.error("File processing failed");
            return;
        }
        const index = files.findIndex(f => f.id === file.id);
        setSelectedIndex(index);

        console.log(file);
        setShowFileBool(true);
    }

    return (
        <div className='w-full h-full flex flex-col items-start p-14'>
            <p className='mb-3'>Details for user: {userId}</p>
            <p className='mb-3'>Email address: {emailAddress}</p>
            <p className='mb-3'>Pages: {numberOfCredits} pages</p>

            <ModalForViewFile open={showFileBool} setOpen={setShowFileBool} >
                <ViewFile
                    closeModal={() => {
                        setShowFileBool(false);
                        setSelectedIndex(null);
                    }}
                    classificationId={selectedClassification.id}
                    classification={selectedClassification}
                    batchId={selectedBatch.id}
                    fileIds={files.map(file => file.id)}
                    firstIndex={selectedIndex}
                />
            </ModalForViewFile>

            <nav className="flex mb-5" aria-label="Breadcrumb">
                <ol className="flex space-x-4 rounded-md bg-white px-6 shadow">
                    <li className="flex h-11">
                        <div className="flex items-center">
                            <button
                                onClick={() => changeView('classifications')}
                                className="text-sm font-medium text-gray-500 hover:text-gray-700"
                            >
                                Classifications
                            </button>
                        </div>
                    </li>
                    {(viewType === 'batches' || viewType === 'batch') &&
                        <li className="flex">
                            <div className="flex items-center">
                                <svg
                                    className="h-full w-6 flex-shrink-0 text-gray-200"
                                    viewBox="0 0 24 44"
                                    preserveAspectRatio="none"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
                                </svg>
                                <button
                                    onClick={() => changeView('batches')}
                                    className={classNames(viewType === 'batches' ? "text-indigo-500 hover:text-indigo-700" : "", "ml-4 text-sm font-medium text-gray-500 hover:text-gray-700")}
                                >
                                    Batches
                                </button>
                            </div>
                        </li>}
                    {viewType === 'batch' &&
                        <li className="flex">
                            <div className="flex items-center">
                                <svg
                                    className="h-full w-6 flex-shrink-0 text-gray-200"
                                    viewBox="0 0 24 44"
                                    preserveAspectRatio="none"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
                                </svg>
                                <button
                                    onClick={() => changeView('batch')}
                                    className={classNames(viewType === 'batch' ? "text-indigo-500 hover:text-indigo-700" : "", "ml-4 text-sm font-medium text-gray-500 hover:text-gray-700")}
                                >
                                    Batch
                                </button>
                            </div>
                        </li>}
                </ol>
            </nav>

            {viewType === 'classifications' &&
                <div className='w-full'>
                    <ul className="divide-y mt-6 divide-gray-100 overflow-hidden bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
                        {classificationsList.map((classification) => (
                            <li key={classification.id} className="cursor-pointer gap-x-6 hover:bg-gray-50 px-4 py-5 sm:px-6 relative flex justify-between">
                                <button
                                    onClick={() => changeViewType({
                                        type: 'batches',
                                        classificationId: classification.id
                                    })}
                                    className='w-full relative flex justify-between'
                                >
                                    <div className="flex min-w-0 gap-x-4">
                                        <div className="min-w-0 flex-auto">
                                            <div className="flex items-start">
                                                <p className="text-sm font-semibold leading-6 text-gray-900 mr-3">
                                                    {classification.name}
                                                </p>
                                                <p className={classNames(
                                                    statuses[classification.status],
                                                    'rounded-md whitespace-nowrap mt-0.5 px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset'
                                                )}>
                                                    {classification.status}
                                                </p>
                                            </div>
                                            <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
                                                {(classification.description !== null && classification.description.length > 0) &&
                                                    <>
                                                        <p className="whitespace-nowrap">
                                                            {classification.description}
                                                        </p>
                                                        <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                                                            <circle cx={1} cy={1} r={1} />
                                                        </svg>
                                                    </>
                                                }
                                                <p className="truncate">
                                                    {classification.documentTypes.length} Document {classification.documentTypes.length === 1 ? 'Type' : 'Types'}
                                                </p>
                                                <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                                                    <circle cx={1} cy={1} r={1} />
                                                </svg>
                                                <p className="truncate">
                                                    {getLinkedTemplates(classification)} Linked Extraction {getLinkedTemplates(classification) === 1 ? 'Template' : 'Templates'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-x-4">
                                        <ChevronRightIcon className="h-5 w-5 flex-none text-gray-400" aria-hidden="true" />
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>

                    {classificationsList.length === 0 &&
                        <div className="mt-4 flex-1 flex flex-col items-center justify-center h-full rounded-lg bg-gray-100 p-8">
                            <div className="text-center">
                                <h2 className="text-xl font-semibold text-gray-900">No classifications yet</h2>
                                <p className="mt-1.5 text-sm text-gray-600">
                                    Get started by creating a new classification.
                                </p>
                            </div>
                        </div>
                    }
                </div>
            }

            {viewType === 'batches' &&
                <div className='w-full'>
                    <div className="">
                        <ClassificationHeading
                            classificationData={selectedClassification}
                        />
                    </div>

                    <div>
                        <PaginationAdmin
                            numberOfPages={noOfPagesBatch}
                            currentPageNumber={currentBatchPage}
                            previousButton={previousBatchPage}
                            nextButton={nextBatchPage}
                            selectPage={() => { }}
                            itemsPerPage={0}
                            setItemsPerPage={() => { }}
                        />
                    </div>

                    <div className="mt-5 flow-root">
                        {/* Existing batches table */}
                        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-300">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                                    Id
                                                </th>
                                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                    No. of documents
                                                </th>
                                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                    Created at
                                                </th>
                                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                    Status
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {batchesList.map((batch) => (
                                                <tr
                                                    key={batch.id}
                                                    className="hover:bg-gray-50 cursor-pointer"
                                                    onClick={() => changeViewType({
                                                        type: 'batch',
                                                        classificationId: selectedClassification.id,
                                                        batchId: batch.id
                                                    })}
                                                >
                                                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                                        {batch.id}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {batch.numberOfDocuments} {batch.numberOfDocuments === 1 ? 'document' : 'documents'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {formatExtractionsDate(batch.startDateTime)}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        <p
                                                            className={classNames(
                                                                statuses[batch.status],
                                                                'rounded-md inline-block mt-0.5 px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset'
                                                            )}
                                                        >
                                                            {batch.status}
                                                        </p>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }

            {viewType === 'batch' &&
                <div className='w-full'>
                    <div className="">
                        <BatchHeading
                            batchName={"Batch"}
                            batchCreatedAt={formatExtractionsDate(selectedBatch.startTime)}
                            classificationId={selectedClassification.id}
                            batchId={selectedBatch.id}
                        />
                    </div>

                    {/* <div className="mt-5 overflow-y-auto max-h-[200px]">
                        <SelectFileStatus
                            tabs={tabs}
                            currentTab={currentTab}
                            changeTab={changeTab}
                        />
                    </div> */}

                    <div className="mt-2 flow-root">
                        <div className="-mx-4 -my-2 overflow-x-auto overflow-y-hidden sm:-mx-6 lg:-mx-8">
                            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                                <div className="relative">
                                    <table className={classNames("min-w-full table-fixed divide-y divide-gray-300",
                                        files.length <= 0 ? 'hidden' : ''
                                    )}>
                                        <thead>
                                            <tr>
                                                <th scope="col" className="py-2.5 pr-3 text-left text-sm font-semibold text-gray-900">
                                                    {isChangingTab && <Skeleton width={80} height={20} className="" />}
                                                    {!isChangingTab && "File name".toUpperCase()}
                                                </th>
                                                <th scope="col" className="px-3 py-2.5 text-left text-sm font-semibold text-gray-900">
                                                    {isChangingTab && <Skeleton width={80} height={20} className="" />}
                                                    {!isChangingTab && "Status".toUpperCase()}
                                                </th>
                                                <th scope="col" className="px-3 py-2.5 text-left text-sm font-semibold text-gray-900">
                                                    {isChangingTab && <Skeleton width={80} height={20} className="" />}
                                                    {!isChangingTab && "Document Type".toUpperCase()}
                                                </th>
                                                <th scope="col" className="px-3 py-2.5 text-left text-sm font-semibold text-gray-900">
                                                    {isChangingTab && <Skeleton width={80} height={20} className="" />}
                                                    {!isChangingTab && (
                                                        <button
                                                            onClick={() => handleSort('confidence')}
                                                            className="group inline-flex items-center hover:text-gray-700 hover:bg-gray-50 px-2 py-1 rounded transition-colors duration-150"
                                                        >
                                                            {"Confidence".toUpperCase()}
                                                            <span className="ml-2 flex-none rounded text-gray-500">
                                                                {sortField === 'confidence' ? (
                                                                    sortDirection === 'desc' ? (
                                                                        <SortChevronDownIcon className="h-4 w-4 text-indigo-700 font-bold" aria-hidden="true" />
                                                                    ) : (
                                                                        <ChevronUpIcon className="h-4 w-4 text-indigo-700 font-bold" aria-hidden="true" />
                                                                    )
                                                                ) : (
                                                                    <div className="relative h-4 w-4">
                                                                        <ChevronUpIcon className="absolute top-0 h-2.5 w-4" aria-hidden="true" />
                                                                        <SortChevronDownIcon className="absolute bottom-0 h-2.5 w-4" aria-hidden="true" />
                                                                    </div>
                                                                )}
                                                            </span>
                                                        </button>
                                                    )}
                                                </th>
                                                <th scope="col" className="px-3 py-2.5 text-left text-sm font-semibold text-gray-900">
                                                    {isChangingTab && <Skeleton width={80} height={20} className="" />}
                                                    {!isChangingTab && "Extraction".toUpperCase()}
                                                </th>
                                                {files.length <= 0 &&
                                                    <th scope="col" className="relative py-2.5 pl-3 pr-4 sm:pr-3">
                                                        <span className="sr-only">Edit</span>
                                                    </th>
                                                }
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {isChangingTab && (
                                                <tr>
                                                    <td colSpan="6" className="py-8">
                                                        <Skeleton className='mb-3' height={20} count={10} />
                                                    </td>
                                                </tr>
                                            )}

                                            {!isChangingTab && files.map((file, index) => (
                                                <tr key={file.id} className={files.includes(file) ? 'bg-gray-50' : undefined}>
                                                    <td
                                                        className={classNames(
                                                            'whitespace-nowrap py-4 pr-3 text-sm font-medium',
                                                            files.includes(file) ? 'text-indigo-600' : 'text-gray-900'
                                                        )}
                                                    >
                                                        <div className='flex items-center group'>
                                                            <button onClick={() => {
                                                                openFileResult(file)
                                                            }}>
                                                                <span className='group-hover:underline group-hover:text-indigo-600 mr-1.5 cursor-pointer'>
                                                                    {file.name}
                                                                </span>
                                                            </button>
                                                            <button
                                                                onClick={() => openFileResult(file)}
                                                                className="inline-flex items-center justify-center h-6 w-6 rounded-full text-gray-400 group-hover:text-gray-500 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-indigo-500"
                                                            >
                                                                <FontAwesomeIcon icon={IconEdit} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" >
                                                        <span className='text-red-300'>
                                                            {file.status === 'error' ? 'error' : ''}
                                                            {file.status === 'error' ? (
                                                                file.errorMessage !== undefined ? (': ' + file.errorMessage) : ''
                                                            ) : ''}
                                                        </span>
                                                        <span className='text-orange-300'>
                                                            {file.status === 'waiting' ? 'processing' : ''}
                                                        </span>
                                                        <span className=''>
                                                            {file.status === 'processed' ? 'processed' : ''}
                                                        </span>
                                                        <span className=''>
                                                            {file.status === 'confirmed' ? 'confirmed' : ''}
                                                        </span>
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" >
                                                        {file.result?.documentType ? (
                                                            <div
                                                                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-sm font-medium`}>
                                                                {file.result?.documentType}
                                                            </div>
                                                        ) : (
                                                            <div className="inline-flex items-center px-2 py-0.5 text-sm font-medium">
                                                                -
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" >
                                                        {file.result?.confidence !== undefined ? (
                                                            <div
                                                                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-sm font-medium
                            ${file.result?.confidence >= 0.9
                                                                        ? "text-green-700 border-green-200"
                                                                        : file.result?.confidence >= 0.8
                                                                            ? "text-amber-700 border-amber-200"
                                                                            : "text-red-700 border-red-200"
                                                                    }`}
                                                            >
                                                                {file.result?.confidence * 100 + "%"}
                                                            </div>
                                                        ) : (
                                                            <div className="inline-flex items-center px-2 py-0.5 text-sm font-medium">
                                                                -
                                                            </div>
                                                        )}
                                                    </td>
                                                    {file.extraction.errorMessage || file.extraction.extractionId ? (
                                                        <td className="pl-2" >
                                                            <div className="col-span-2 flex items-center">
                                                                <div>
                                                                    {file.extraction.extractionId && file.extraction.name && (
                                                                        <div
                                                                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-sm font-medium mb-1 bg-gray-100`}
                                                                        >
                                                                            {file.extraction.name.length > 40 ? (
                                                                                <span title={file.extraction.name}>
                                                                                    {file.extraction.name.substring(0, 40)}...
                                                                                </span>
                                                                            ) : file.extraction.name}
                                                                        </div>
                                                                    )}
                                                                    <div className="flex items-center text-xs">
                                                                        {file.extraction.status === "processed" ? (
                                                                            <>
                                                                                <CheckCircle className="h-3 w-3 text-green-600 mr-1" />
                                                                                <span className="text-green-600">Completed</span>
                                                                            </>
                                                                        ) : file.extraction.status === "waiting" ? (
                                                                            <>
                                                                                <AlertCircle className="h-3 w-3 text-amber-600 mr-1" />
                                                                                <span className="text-amber-600">In Progress</span>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <AlertCircle className="h-4 w-4 text-red-600 mr-1" />
                                                                                <span className="text-red-600 text-sm">{file.extraction.errorMessage || 'Error'}</span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    ) : file.result?.documentType === 'other' || file.status === "waiting" ? (
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" >

                                                        </td>
                                                    ) : (
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" >
                                                            No extraction linked
                                                        </td>
                                                    )
                                                    }

                                                    {/* {files.length <= 0 &&
                                                        <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3 relative">
                                                            <DropdownWithDividerAndIcons
                                                                className="overflow-visible"
                                                                file={file}
                                                                exportAs={() => {
                                                                    setFileIdForExport(file.id);
                                                                    setShowExportModal(true);
                                                                }}
                                                                reloadFile={reloadFile}
                                                                deleteFile={({ fileId }) => {
                                                                    setShowDeleteFile(true);
                                                                    setSelectedFileIdDelete(fileId);
                                                                }}
                                                                initialButton={
                                                                    <Menu.Button className="flex items-center">
                                                                        <span className="text-indigo-600 hover:text-indigo-900">
                                                                            Options<span className="sr-only">, {file.name}</span>
                                                                        </span>
                                                                    </Menu.Button>
                                                                }
                                                            />
                                                        </td>
                                                    } */}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='mt-20'></div>
                </div>
            }
        </div>
    )
}

function ClassificationHeading({
    classificationData,
}) {
    const documentTypes = classificationData?.documentTypes || [];
    const classificationId = classificationData.id;

    return (
        <>
            <div className="lg:flex lg:items-center lg:justify-between">
                <div className="min-w-0 flex-1">
                    <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
                        {!classificationData.name ? <Skeleton width={300} /> : classificationData.name}
                    </h2>
                    <div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-6">
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                            <CalendarIcon className="mr-1.5 h-6 w-6 flex-shrink-0 text-gray-400" aria-hidden="true" />
                            {formatDate(classificationData.createdAt)}
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                            <CodeBracketIcon className="mr-1.5 h-6 w-6 flex-shrink-0 text-gray-400" aria-hidden="true" />
                            <span><span className="font-semibold">Classification ID:</span>{" "}{classificationId}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Document Types Header*/}
            <div className="my-6 border rounded-lg shadow-sm">
                <div className="p-4 pb-0">
                    <h3 className="text-base font-semibold leading-6 text-gray-900">
                        Document Classification Outputs
                    </h3>
                    <p className="text-sm text-gray-500">
                        Define expected document outputs to classify your documents. Each document output can be linked to a data extraction template.
                    </p>
                </div>
                <div className="p-4 pt-3">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {documentTypes.length === 0 ? (
                            // Show 4 skeleton items while loading
                            [...Array(4)].map((_, i) => (
                                <div key={i} className="flex items-center gap-2 border rounded-md p-2">
                                    <Skeleton circle width={16} height={16} className="flex-shrink-0" />
                                    <div className="min-w-0 space-y-1" style={{ lineHeight: '0' }}>
                                        <Skeleton width={100} height={12} className="m-1" /> {/* Match font-medium text-sm */}
                                        <Skeleton width={40} height={10} className="m-1" /> {/* Match text-xs */}
                                    </div>
                                </div>
                            ))
                        ) : (
                            documentTypes.map((docType) => (

                                <div
                                    key={docType.name + docType.description}
                                    className="flex items-center gap-2 border rounded-md p-2 cursor-pointer hover:bg-gray-50"
                                >
                                    <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium text-sm truncate">{docType.name}</div>
                                        {docType.extractionId ? (
                                            <div className="flex items-center text-xs text-green-600">
                                                <LinkIcon className="h-3 w-3 mr-1" />
                                                <span className="truncate">Linked</span>
                                            </div>
                                        ) : (
                                            <div className="text-xs text-gray-400">Not linked</div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

        </>
    );
}

function BatchHeading({
    batchName,
    batchCreatedAt,
    classificationId,
    batchId
}) {
    return (
        <div className="lg:flex lg:items-center lg:justify-between">
            <div className="min-w-0 flex-1">
                <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
                    {batchName}
                </h2>
                <div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-6">
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                        <CalendarIcon className="mr-1.5 h-6 w-6 flex-shrink-0 text-gray-400" aria-hidden="true" />
                        {batchCreatedAt}
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                        <CodeBracketIcon className="mr-1.5 h-6 w-6 flex-shrink-0 text-gray-400" aria-hidden="true" />
                        <span>
                            <span className="font-semibold">Classification ID:</span>{" "}{classificationId}
                        </span>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                        <CodeBracketIcon className="mr-1.5 h-6 w-6 flex-shrink-0 text-gray-400" aria-hidden="true" />
                        <span>
                            <span className="font-semibold">Batch ID:</span>{" "}{batchId}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}